import React from "react";
import deepEqual from "fast-deep-equal";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { PokerCard } from "./PokerCard";
import { PokerTableCenter } from "./PokerTableCenter";
import { useWindowSize } from "../hooks/useWindowSize";
import { GameState } from "@/types/poker";

interface PokerPlayersProps {
  state: GameState | null;
  playerId: string | null;
}

const PokerPlayersComponent = ({ state, playerId }: PokerPlayersProps) => {
  const { width = 0, height = 0 } = useWindowSize();

  if (!state?.players?.length) {
    return (
      <div className="flex h-72 items-center justify-center rounded-3xl border border-dashed border-white/10 bg-gradient-to-br from-primary/20 to-background text-sm text-muted-foreground">
        Waiting for players...
      </div>
    );
  }

  const numPlayers = state.players.length;
  const containerWidth = Math.min(Math.max(width ? width - 48 : 360, 320), 1100);
  const isMobile = width < 768;
  const tableHeight = isMobile && height ? height * 0.63 : 360;
  const seatWidth = isMobile ? 80 : 100;
  const seatHeight = isMobile ? 80 : 95;
  const youIndex = state.players.findIndex((player) => player?.id === playerId);
  const angleOffset = youIndex >= 0 ? Math.PI - (youIndex / numPlayers) * 2 * Math.PI : 0;

  return (
    <div className="flex w-full justify-center">
      <div
        className="relative"
        style={{ width: containerWidth, height: tableHeight }}
      >
        <div className=" absolute inset-0 rounded-[48%] bg-gradient-to-b from-primary/70 to-primary text-white shadow-2xl border-[20px] border-black" />
        <div className="absolute inset-6 rounded-[45%] bg-black/30 blur-2xl" />

        <div className="absolute inset-0 px-10 py-12">
          <PokerTableCenter state={state} />
        </div>

        {state.players.map((player, index) => {
          if (!player) {
            return null;
          }
          const angle = (index / numPlayers) * 2 * Math.PI - Math.PI / 2 + angleOffset;
          const radiusX = containerWidth / 2 - seatWidth / 2;
          const radiusY = tableHeight / 2 - seatHeight / 2;

          const x = containerWidth / 2 + radiusX * Math.cos(angle);
          const y = tableHeight / 2 + radiusY * Math.sin(angle);
          const isYou = player.id === playerId;
          const isActor = index === state.actionIndex;
          const isDealer = index === state.dealerIndex;

          const betRadiusX = radiusX * 0.7;
          const betRadiusY = radiusY * 0.7;
          const betX = containerWidth / 2 + betRadiusX * Math.cos(angle);
          const betY = tableHeight / 2 + betRadiusY * Math.sin(angle);
          const betAmount = state.roundBets?.[player.id] ?? 0;

          return (
            <React.Fragment key={player.id}>
              <div
                className={cn(
                  "absolute flex flex-col items-center gap-1 rounded-lg border px-3 py-2 text-xs text-white backdrop-blur transition-all",
                  "border-white/20 bg-black/60 shadow-[0_10px_25px_rgba(0,0,0,0.35)]",
                  isActor && "border-amber-400",
                  player.folded && "opacity-50"
                )}
                style={{
                  left: x,
                  top: y,
                  width: seatWidth,
                  minHeight: seatHeight,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <div className="flex items-center justify-center gap-1 h-10">
                  {player.hand?.map((card, idx: number) => (
                    <PokerCard key={idx} card={card} hidden={!isYou && state.phase !== 'showdown'} compact />
                  ))}
                </div>

                <div className="relative w-full text-center">
                  <span className="font-semibold text-white">{player.stack}</span>
                  <p className="text-[10px] text-white/70 truncate">{player.name}</p>
                </div>

                <div className="absolute -top-2 -right-2 flex flex-col items-end gap-1">
                   {isDealer && (
                    <Badge className="bg-white text-black w-5 h-5 flex items-center justify-center p-0 font-bold text-xs shadow-md">D</Badge>
                  )}
                  {player.allin && (
                    <Badge className="bg-red-600 text-white px-1.5 py-0.5 text-[9px] uppercase">All-in</Badge>
                  )}
                </div>
              </div>

              {betAmount > 0 && (
                <div
                  className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1 text-xs text-white"
                  style={{ left: betX, top: betY }}
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60 p-4 text-center text-[12px] font-bold ring-1 ring-amber-400/50">
                    {betAmount}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export const PokerPlayers = React.memo(PokerPlayersComponent, (prevProps, nextProps) => {
  // Deep compare the state object. The playerId is a primitive, so a simple check is fine.
  return deepEqual(prevProps.state, nextProps.state) && prevProps.playerId === nextProps.playerId;
});
